import React, { useEffect, useState, useRef } from "react";
import { fabric } from "fabric";
import {useSelector } from "react-redux";
import { FabricJSCanvas, useFabricJSEditor } from "fabricjs-react";
import {getImageProxy,POSThttpMethod } from "../api/saveErasedImage";
import { currentSelectedLanguage } from "../features/selectedLanguage/languageSlice";
import fontData from '../assets/fonts.json';

function FabricCanvas({ 
  panel_num,
  comic_file_id,
  initialColor = "#35", 
  backgroundImageUrl,
  textBoxes = [],
  handleBBoxClick,
  selectedBoxIndex = null
}) {
  const { editor, onReady } = useFabricJSEditor();
  const containerRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const boxRefs = useRef([]);
  const toolbarRef = useRef(null);
  const lang = useSelector(currentSelectedLanguage);

  const history = [];
  const [color, setColor] = useState(initialColor);
  const [fontFamily, setFontFamily] = useState('Arial');
  const [fontSize, setFontSize] = useState(20);
  const [cropImage, setCropImage] = useState(true);
  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 0
  });
  const [imagedimensions, setImageDimensions] = useState({
    width: 0,
    height: 0
  });  
  const [canvasLoaded, setCanvasLoaded] = useState(false);

  const [currentBackgroundUrl, setCurrentBackgroundUrl] = useState(null);



  const existingFontFamilies = [
    { name: 'Arial', url: null }, // System font, no URL needed
    { name: 'Times New Roman', url: null },
    { name: 'Courier New', url: null },
    { name: 'Roboto', url: null },
    { name: 'Open Sans', url: null },
    { name: 'Lato', url: null },
    { name: 'Montserrat', url: null },
    { name: 'Poppins', url: null },
    { name: 'Playfair Display', url: null },
    { name: 'Raleway', url: null },
    { name: 'CCAbsolutelyFabulous', url: '/fonts/CCAbsolutelyFabulous.ttf' },
    // Add more fonts here
  ];
  const fontSizes = [12, 14, 16, 18, 20, 24, 28, 32, 36, 42, 48, 60];

  const fontFamilies = [...existingFontFamilies, ...fontData];

  // Style for the toolbar buttons
  const buttonStyle = {
    padding: '8px 12px',
    backgroundColor: '#f8f9fa',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none',
    color: '#333',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '32px',
  };

  const buttonHoverStyle = {
    backgroundColor: '#e9ecef',
    borderColor: '#ced4da',
  };

  // Grouped button style (for tools that belong together)
  const groupedButtonStyle = {
    ...buttonStyle,
    borderRadius: '0',
    margin: '0',
    borderRight: 'none',
  };

  const firstGroupButtonStyle = {
    ...groupedButtonStyle,
    borderTopLeftRadius: '4px',
    borderBottomLeftRadius: '4px',
  };

  const lastGroupButtonStyle = {
    ...groupedButtonStyle,
    borderRight: '1px solid #ddd',
    borderTopRightRadius: '4px',
    borderBottomRightRadius: '4px',
  };

  // Specific styles for different button types
  const primaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#4285f4',
    borderColor: '#4285f4',
    color: 'white',
  };

  const dangerButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#dc3545',
    borderColor: '#dc3545',
    color: 'white',
  };

  // Style for input groups
  const inputGroupStyle = {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
    border: '1px solid #ddd',
    borderRadius: '4px',
    padding: '3px 8px',
  };

  const labelStyle = {
    fontSize: '14px',
    fontWeight: '500',
    marginRight: '8px',
    color: '#495057',
  };
  
  const [hoveredButton, setHoveredButton] = useState(null);
  const [brushSize, setBrushSize] = useState(5);
  const [isBrushMode, setIsBrushMode] = useState(false);
  
  //brush sizes
  const brushSizes = [1, 2, 3, 5, 8, 12, 16, 20, 24, 30];

  // Render font options with their actual fonts
  const renderFontOption = (font) => {
    return (
      <option 
        key={font.name} 
        value={font.name} 
        style={{ 
          fontFamily: font.name,
          padding: '8px',
          fontSize: '16px'
        }}
      >
        {font.name}
      </option>
    );
  };

  const hideBoundingBoxes = () => {
    if (!editor || !editor.canvas) return [];
    
    const boundingBoxes = editor.canvas._objects.filter(obj => 
      obj.type === 'rect' && obj.id && obj.id.startsWith('textbox_')
    );
    
    boundingBoxes.forEach(box => {
      box.visible = false;
    });
    
    editor.canvas.renderAll();
    return boundingBoxes;
  };
  
  const showBoundingBoxes = (boundingBoxes = null) => {
    if (!editor || !editor.canvas) return;
    const boxes = boundingBoxes || editor.canvas._objects.filter(obj => 
      obj.type === 'rect' && obj.id && obj.id.startsWith('textbox_')
    );
    
    boxes.forEach(box => {
      box.visible = true;
    });
    
    editor.canvas.renderAll();
  };

  const addFilledRectangle = () => {
    if (!editor || !containerRef.current) return;
    
    const scrollTop = containerRef.current.scrollTop;
    const scrollLeft = containerRef.current.scrollLeft;
    
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    
    const centerX = scrollLeft + containerWidth / 2;
    const centerY = scrollTop + containerHeight / 2;
    
    const coloredBox = new fabric.Rect({
      left: centerX,
      top: centerY,
      fill: color, 
      width: 100,  
      height: 40,  
      stroke: color, 
      strokeWidth: 1,
      opacity: 1.0, 
      id: 'color_box_' + new Date().getTime(),
      excludeFromExport: false,
      selectable: true,
      transparentCorners: false,
    });
    
    editor.canvas.add(coloredBox);
    editor.canvas.setActiveObject(coloredBox);
    editor.canvas.renderAll();
  };

  // Calculate container dimensions on mount and resize
  useEffect(() => {
    const updateContainerDimensions = () => {
      if (containerRef.current) {
        const toolbarHeight = toolbarRef.current ? toolbarRef.current.offsetHeight : 40;
        const { clientWidth, clientHeight } = containerRef.current.parentElement;
        
        setContainerDimensions({
          width: clientWidth, 
          height: clientHeight - toolbarHeight // Subtract toolbar height
        });
      }
    };

    // Initial update
    setTimeout(updateContainerDimensions, 100);
    
    // Update on resize
    window.addEventListener('resize', updateContainerDimensions);
    return () => window.removeEventListener('resize', updateContainerDimensions);
  }, []);

  // Setup canvas event listeners
  useEffect(() => {
    if (!editor || !fabric) {
      return;
    }
    
    // Reset event listeners
    editor.canvas.__eventListeners = {};
    
    if (!cropImage) {
      return;
    }
    if (!editor.canvas.__eventListeners["mouse:down"]) {
      editor.canvas.on("mouse:down", function (opt) {
        var evt = opt.e;
        if (evt.ctrlKey === true) {
          this.isDragging = true;
          this.selection = false;
          this.lastPosX = evt.clientX;
          this.lastPosY = evt.clientY;
        }
      });
    }

    if (!editor.canvas.__eventListeners["mouse:move"]) {
      editor.canvas.on("mouse:move", function (opt) {
        if (this.isDragging) {
          var e = opt.e;
          var vpt = this.viewportTransform;
          vpt[4] += e.clientX - this.lastPosX;
          vpt[5] += e.clientY - this.lastPosY;
          this.requestRenderAll();
          this.lastPosX = e.clientX;
          this.lastPosY = e.clientY;
        }
      });
    }

    if (!editor.canvas.__eventListeners["mouse:up"]) {
      editor.canvas.on("mouse:up", function (opt) {
        this.setViewportTransform(this.viewportTransform);
        this.isDragging = false;
        this.selection = true;
      });
    }

    if (!editor.canvas.__eventListeners["mouse:down:before"]) {
      editor.canvas.on("mouse:down:before", function(opt) {
        // If right mouse button is clicked
        if (opt.e.button === 2) {
          opt.e.preventDefault(); 
        
          const pointer = editor.canvas.getPointer(opt.e);
          const objects = editor.canvas.getObjects().filter(obj => {
            // Only include objects that aren't text boxes and are under the pointer
            return !(obj.id && obj.id.startsWith('textbox_')) && 
                   obj.containsPoint(pointer);
          });
          

          if (objects.length > 0) {
            let nextIndex = 0;
            const activeObject = editor.canvas.getActiveObject();
            
            if (activeObject) {
              const currentIndex = objects.findIndex(obj => obj === activeObject);
              nextIndex = (currentIndex + 1) % objects.length;
            }
            
            // Select the next object
            editor.canvas.setActiveObject(objects[nextIndex]);
            editor.canvas.renderAll();
          }
        }
      });
    }

    editor.canvas.renderAll();
  }, [editor, cropImage]);

  useEffect(() => {
    if (!editor || !editor.canvas) return;
    
    const activeObject = editor.canvas.getActiveObject();
    if (activeObject && activeObject.type === 'i-text') {
      activeObject.set({
        fontFamily: fontFamily,
        fontSize: fontSize,
        fill: color 
      });
      editor.canvas.renderAll();
    }
  }, [fontFamily, fontSize, color, editor]);

  const loadImageToCanvas = (url) => {
    fabric.Image.fromURL(
      url,
      (image) => {
        // Get original image dimensions
        const imgWidth = image.width;
        const imgHeight = image.height;
        
        // Store original dimensions in state
        setImageDimensions({
          width: imgWidth,
          height: imgHeight
        });
        
        // The new canvas height and width should be same as image dimensions
        const canvasWidth = imgWidth;
        const canvasHeight = imgHeight;

        // Reset any zoom/pan that might have been applied
        editor.canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
        
        // Disable selection for background
        editor.canvas.selection = false;
        editor.canvas.hoverCursor = 'default';

        // Set canvas dimensions
        editor.canvas.setWidth(canvasWidth);
        editor.canvas.setHeight(canvasHeight);
        
        // Scale image to fit canvas exactly
        image.set({
          scaleX: 1,
          scaleY: 1,
          left: 0,
          top: 0,
          originX: 'left',
          originY: 'top'
        });

        // Set as background (replacing any existing background)
        editor.canvas.setBackgroundImage(
          image,
          editor.canvas.renderAll.bind(editor.canvas)
        );
        
        addTextBoxes(editor.canvas.width, editor.canvas.height);
        setCanvasLoaded(true);
      },
      {
        crossOrigin: 'anonymous'
      }
    );
  };

  const addBackground = async () => {
    if (!editor || !fabric) {
      return;
    }
    
    try {
      // If we already have a URL in state, use that instead of fetching again
      if (currentBackgroundUrl) {
        loadImageToCanvas(currentBackgroundUrl);
        return;
      }
      
      // Otherwise, fetch the URL from the background image URL prop
      const response = await getImageProxy(backgroundImageUrl);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      

      setCurrentBackgroundUrl(objectUrl);
      
      loadImageToCanvas(objectUrl);
    } catch (error) {
      console.error("Error loading background image:", error);
    }
  };


  const addTextBoxes = (canvasWidth, canvasHeight) => {
    if (!editor || !textBoxes || textBoxes.length === 0) return;
    
    // Clear existing box references
    boxRefs.current = [];
    
    // Remove any existing boxes first
    const existingBoxes = editor.canvas.getObjects().filter(obj => 
      obj.type === 'rect' && obj.id && obj.id.startsWith('textbox_')
    );
    existingBoxes.forEach(box => editor.canvas.remove(box));
    
    // Add boxes for each text box
    textBoxes.forEach((box, index) => {
      const [left, top, right, bottom] = box;
      
      // Scale coordinates according to the current canvas dimensions
      const scaledLeft = left ;
      const scaledTop = top ;
      const scaledWidth = (right - left) ;
      const scaledHeight = (bottom - top);
      
      const rect = new fabric.Rect({
        left: scaledLeft,
        top: scaledTop,
        width: scaledWidth,
        height: scaledHeight,
        fill: 'transparent',
        stroke: index === selectedBoxIndex ? 'blue' : 'red',
        strokeWidth: 2,
        selectable: false,
        evented: true,
        id: `textbox_${index}`
      });
      
      // Add event handlers (keep your existing handlers)
      rect.on('mouseover', () => {
        rect.set('stroke', 'blue');
        editor.canvas.renderAll();
      });
      
      rect.on('mouseout', () => {
        if (index !== selectedBoxIndex) {
          rect.set('stroke', 'red');
          editor.canvas.renderAll();
        }
      });
      
      rect.on('mousedown', () => {
        if (handleBBoxClick) {
          handleBBoxClick(index);
        }
      });
      
      editor.canvas.add(rect);
      boxRefs.current.push(rect);
    });
    
    editor.canvas.renderAll();
  };

  useEffect(() => {
    if (!editor || !fabric || boxRefs.current.length === 0) return;
    
    boxRefs.current.forEach((rect, index) => {
      if (rect) {
        rect.set('stroke', index === selectedBoxIndex ? 'blue' : 'red');
      }
    });
    
    editor.canvas.renderAll();
  }, [selectedBoxIndex, editor]);

  useEffect(() => {
    if (!editor || !fabric) return;
    
    if (editor.canvas.backgroundImage) {
      addTextBoxes(editor.canvas.width, editor.canvas.height);
    }
  }, [textBoxes, editor]);

  useEffect(() => {
    if (!editor || !fabric || containerDimensions.width === 0) {
      return;
    }
    
    // Initialize with container dimensions if available, else use initialCanvas dimensions
    addBackground();
  }, [containerDimensions]);

  const toggleDraw = () => {
    editor.canvas.isDrawingMode = !editor.canvas.isDrawingMode;
  };
  
  const toggleBrushMode = () => {
    if (!editor) return;
    
    const newBrushMode = !isBrushMode;
    setIsBrushMode(newBrushMode);
    
    editor.canvas.isDrawingMode = newBrushMode;
    
    if (newBrushMode) {
      setupBrush();
    }
  };
  
  const setupBrush = () => {
    if (!editor) return;
    
    // Create a pencil brush
    const pencilBrush = new fabric.PencilBrush(editor.canvas);
    pencilBrush.color = color;
    pencilBrush.width = brushSize;
    
    // Set the brush to the canvas
    editor.canvas.freeDrawingBrush = pencilBrush;
  };
  
  const handleBrushSizeChange = (e) => {
    const newSize = parseInt(e.target.value);
    setBrushSize(newSize);
    
    if (editor && isBrushMode) {
      editor.canvas.freeDrawingBrush.width = newSize;
    }
  };

  const clear = () => {
    // Remove all objects except bounding boxes
    const objectsToKeep = editor.canvas._objects.filter(obj => 
      obj.type === 'rect' && obj.id && obj.id.startsWith('textbox_')
    );
    
    editor.canvas._objects.splice(0, editor.canvas._objects.length);
    
    // Add back the bounding boxes
    objectsToKeep.forEach(obj => {
      editor.canvas.add(obj);
    });
    
    history.splice(0, history.length);
    editor.canvas.renderAll();
  };

  const removeSelectedObject = () => {
    if (!editor || !editor.canvas) return;
    
    const activeObject = editor.canvas.getActiveObject();
    
    // Don't delete text bounding boxes
    if (!activeObject || (activeObject.id && activeObject.id.startsWith('textbox_'))) {
      return;
    }
    
    // If we have a group selection
    if (activeObject.type === 'activeSelection') {
      const objectsToRemove = activeObject._objects.filter(
        obj => !(obj.id && obj.id.startsWith('textbox_'))
      );
      objectsToRemove.forEach(obj => {
        editor.canvas.remove(obj);
      });

      editor.canvas.discardActiveObject();
    } else {
      editor.canvas.remove(activeObject);
    }
    
    editor.canvas.renderAll();
  };

  const handleColorChange = (e) => {
    const newColor = e.target.value;
    setColor(newColor);
    
    if (editor && editor.canvas) {
      const activeObject = editor.canvas.getActiveObject();
      if (activeObject) {
        activeObject.set({ fill: newColor });
        editor.canvas.renderAll();
      }
    }
  };

  const addText = () => {
    if (!editor || !containerRef.current) return;
    
    // Calculations to make sure the new box appears at the center of current canvas
    const scrollTop = containerRef.current.scrollTop;
    const scrollLeft = containerRef.current.scrollLeft;
    
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    
    const centerX = scrollLeft + containerWidth / 2;
    const centerY = scrollTop + containerHeight / 2;
    
    const text = new fabric.IText("Text here", {
      left: centerX,
      top: centerY,
      fontFamily: fontFamily,
      fill: color,
      fontSize: fontSize,
      padding: 10,
      id: 'text_' + new Date().getTime(),
      transparentCorners: false,
      cornerColor: 'rgba(0,0,255,0.5)',
      cornerSize: 10
    });
    
    editor.canvas.add(text);
    editor.canvas.setActiveObject(text);
    editor.canvas.renderAll();
  };

  const handleLanguageImage = async () => {
    if (!editor || !editor.canvas) {
      console.error("Canvas not available");
      return;
    }
  
    try {
      hideBoundingBoxes();
      
      const dataURL = editor.canvas.toDataURL({
        format: 'png',
        quality: 1,
      });
      
      const fetchResponse = await fetch(dataURL);
      const blob = await fetchResponse.blob();
      
      const formData = new FormData();
      formData.append('image', blob, 'lang_image.png');
      formData.append('comic_input_file_id' ,comic_file_id);
      formData.append('language', lang);
      formData.append('panel_num', panel_num);
      formData.append('width', imagedimensions.width);
      formData.append('height', imagedimensions.height);
      
      // Call the API
      const response = await POSThttpMethod(formData,`/api/save-language-image/`,true);
      
      if (response.success) {
        alert("Image saved successfully!");
      } else {
        alert("Failed to save image: " + response.error);
      }
    } catch (error) {
      alert("Error saving image: " + error.message);
    } finally {
      showBoundingBoxes();
    }
  };

  const handleErasedImage = async () => {
    if (!editor || !editor.canvas) {
      console.error("Canvas not available");
      return;
    }
  
    try {
      hideBoundingBoxes();
      
      const dataURL = editor.canvas.toDataURL({
        format: 'png',
        quality: 0.9,
        width: imagedimensions.width,
        height: imagedimensions.height
      });
      
      const fetchResponse = await fetch(dataURL);
      const blob = await fetchResponse.blob();
      
      const formData = new FormData();
      formData.append('erased_image', blob, 'erased_image.png');
      formData.append('comic_input_file_id' ,comic_file_id);
      formData.append('panel_num', panel_num);
      const response = await POSThttpMethod(formData,`/api/save-erased-image/`,true)
      
      if (response.success) {
        alert("Image saved successfully!");
      } else {
        alert("Failed to save image: " + response.error);
      }
    } catch (error) {
      alert("Error saving image: " + error.message);
    } finally {
      showBoundingBoxes();
    }
  };

  const handleLoadImage = async () => {

    try{
      const formData = new FormData();
      formData.append('comic_input_file_id' ,comic_file_id);
      formData.append('panel_num', panel_num);
      formData.append('language', lang);
      const response = await POSThttpMethod(formData,`/api/load-current-image/`,false);

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      setCurrentBackgroundUrl(objectUrl);

      hideBoundingBoxes();
      loadImageToCanvas(objectUrl);
      showBoundingBoxes();
    }
    catch(error) {
      alert("Error loading image: " + error.message);
    }
  }

  const handleEmptyLoadImage = async () => {
    try{
      const formData = new FormData();
      formData.append('comic_input_file_id' ,comic_file_id);
      formData.append('panel_num', panel_num);

      const response = await POSThttpMethod(formData,`/api/load-empty-image/`,false);

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      setCurrentBackgroundUrl(objectUrl);

      hideBoundingBoxes();
      loadImageToCanvas(objectUrl);
      showBoundingBoxes();
    }
    catch(error) {
      alert("Error loading image: " + error.message);
    }
  }


  const loadFont = async (fontName, fontUrl) => {
    if (!fontUrl) return; // Skip loading for system fonts
   console.log(`Loading font: ${fontName} from ${fontUrl}`);
    try {
      const encodedFontUrl = encodeURI(fontUrl);
      const font = new FontFace(fontName, `url(${encodedFontUrl})`);
      await font.load();
      document.fonts.add(font);
      console.log(`Font "${fontName}" loaded successfully`);
    } catch (error) {
      console.error(`Error loading font "${fontName}":`, error);
    }
  };

  const handleFontChange = async (e) => {
    const selectedFontName = e.target.value;
    const selectedFont = fontFamilies.find((font) => font.name === selectedFontName);
  
    if (selectedFont && selectedFont.url) {
      await loadFont(selectedFont.name, selectedFont.url);
    }
   
    setFontFamily(selectedFontName);
  };
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Delete or Backspace key
      if ((e.key === 'Delete' || e.key === 'Backspace') && 
          !['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement.tagName)) {
        removeSelectedObject();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [editor]);
  
  useEffect(() => {
    if (editor && isBrushMode) {
      editor.canvas.freeDrawingBrush.color = color;
    }
  }, [color, isBrushMode, editor]);

  return (
    <>

    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      width: '100%',
      overflow: 'hidden',
      fontFamily: 'Roboto, sans-serif',
    }}>
      <div 
        ref={toolbarRef} 
        className="toolbar" 
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          padding: '10px',
          gap: '10px',
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e1e4e8',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          alignItems: 'center',
        }}
      >
        {/* Drawing tools */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginRight: '16px',
          borderRadius: '4px',
          overflow: 'hidden',
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
        }}>
          <button 
            onClick={addFilledRectangle}
            onMouseEnter={() => setHoveredButton('colorCover')}
            onMouseLeave={() => setHoveredButton(null)}
            style={{
              ...firstGroupButtonStyle,
              ...(hoveredButton === 'colorCover' ? buttonHoverStyle : {})
            }}
          >
            Color Cover
          </button>
          <button 
            onClick={addText}
            onMouseEnter={() => setHoveredButton('addText')}
            onMouseLeave={() => setHoveredButton(null)}
            style={{
              ...groupedButtonStyle,
              ...(hoveredButton === 'addText' ? buttonHoverStyle : {})
            }}
          >
            Add Text
          </button>
          <button 
            onClick={toggleBrushMode}
            onMouseEnter={() => setHoveredButton('brush')}
            onMouseLeave={() => setHoveredButton(null)}
            style={{
              ...lastGroupButtonStyle,
              ...(hoveredButton === 'brush' ? buttonHoverStyle : {}),
              ...(isBrushMode ? {
                backgroundColor: '#d1e7dd',
                borderColor: '#badbcc',
                color: '#0f5132'
              } : {})
            }}
          >
            {isBrushMode ? 'Exit Brush' : 'Brush Mode'}
          </button>
        </div>

        {/* Brush controls - only visible when brush mode is active */}
        {isBrushMode && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            marginRight: '16px',
            padding: '0 10px',
            backgroundColor: '#d1e7dd',
            borderRadius: '4px',
            border: '1px solid #badbcc',
          }}>
            <label style={{ 
              ...labelStyle, 
              color: '#0f5132',
              marginRight: '8px'
            }}>
              Brush Size:
            </label>
            <select 
              value={brushSize}
              onChange={handleBrushSizeChange}
              style={{ 
                padding: '6px 8px',
                border: 'none',
                outline: 'none',
                backgroundColor: 'transparent',
                minWidth: '60px',
                cursor: 'pointer',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                appearance: 'none',
                backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%230f5132%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 8px top 50%',
                backgroundSize: '12px auto',
                paddingRight: '24px',
                color: '#0f5132'
              }}
            >
              {brushSizes.map(size => (
                <option key={size} value={size}>{size}px</option>
              ))}
            </select>
            
            {/* Brush preview */}
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: color,
              marginLeft: '8px',
              border: '1px solid #0f5132',
              transform: `scale(${Math.min(brushSize / 10, 1.5)})`
            }} />
          </div>
        )}

        {/* Edit tools */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginRight: '16px',
          borderRadius: '4px',
          overflow: 'hidden',
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
        }}>
          <button 
            onClick={clear}
            onMouseEnter={() => setHoveredButton('clear')}
            onMouseLeave={() => setHoveredButton(null)}
            style={{
              ...firstGroupButtonStyle,
              ...(hoveredButton === 'clear' ? buttonHoverStyle : {})
            }}
          >
            Clear
          </button>
        </div>

        <button 
          onClick={removeSelectedObject} 
          onMouseEnter={() => setHoveredButton('delete')}
          onMouseLeave={() => setHoveredButton(null)}
          style={{
            ...dangerButtonStyle,
            ...(hoveredButton === 'delete' ? { 
              backgroundColor: '#c82333', 
              borderColor: '#bd2130' 
            } : {})
          }}
        >
          Delete Selected
        </button>

        {/* Color picker */}
        <div style={inputGroupStyle}>
          <label style={labelStyle}>Color:</label>
          <input
            type="color"
            value={color}
            onChange={handleColorChange}
            style={{ 
              width: '30px', 
              height: '30px',
              border: 'none',
              padding: '0',
              background: 'none',
              cursor: 'pointer'
            }}
          />
          <div style={{
            display: 'flex',
            marginLeft: '5px',
            gap: '3px'
          }}>
            {['#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', '#FFFF00'].map(colorValue => (
              <div
                key={colorValue}
                style={{
                  width: '16px',
                  height: '16px',
                  backgroundColor: colorValue,
                  border: color === colorValue ? '2px solid #333' : '1px solid #ddd',
                  cursor: 'pointer',
                  borderRadius: '2px'
                }}
                onClick={() => setColor(colorValue)}
              />
            ))}
          </div>
        </div>

        {/* Font family selector */}
        <div style={inputGroupStyle}>
          <label style={labelStyle}>Font:</label>
          <select 
            value={fontFamily} 
            onChange={handleFontChange}
            style={{ 
              padding: '6px 8px', 
              border: 'none',
              outline: 'none',
              backgroundColor: 'transparent',
              fontFamily: fontFamily,
              minWidth: '180px',
              fontSize: '15px',
              cursor: 'pointer',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              appearance: 'none',
              backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23131313%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 8px top 50%',
              backgroundSize: '12px auto',
              paddingRight: '24px'
            }}
          >
            {fontFamilies.map(renderFontOption)}
          </select>
        </div>

        {/* Font size selector */}
        <div style={inputGroupStyle}>
          <label style={labelStyle}>Size:</label>
          <select 
            value={fontSize} 
            onChange={(e) => setFontSize(parseInt(e.target.value))}
            style={{ 
              padding: '6px 8px',
              border: 'none',
              outline: 'none',
              backgroundColor: 'transparent',
              minWidth: '70px',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              appearance: 'none',
              backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23131313%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 8px top 50%',
              backgroundSize: '12px auto',
              paddingRight: '24px',
              cursor: 'pointer'
            }}
          >
            {fontSizes.map((size) => (
              <option key={size} value={size}>
                {size}px
              </option>
            ))}
          </select>
        </div>

        {/* Export button */}
        <button 
          onClick={handleLanguageImage}
          onMouseEnter={() => setHoveredButton('export')}
          onMouseLeave={() => setHoveredButton(null)}
          style={{
            ...primaryButtonStyle,
            marginLeft: 'auto', // Push to right side
            ...(hoveredButton === 'export' ? { 
              backgroundColor: '#3367d6', 
              borderColor: '#2a56c6' 
            } : {})
          }}
        >
          Save changes
        </button>

        <button 
          onClick={handleErasedImage}
          onMouseEnter={() => setHoveredButton('export')}
          onMouseLeave={() => setHoveredButton(null)}
          style={{
            ...primaryButtonStyle,
            marginLeft: 'auto', // Push to right side
            ...(hoveredButton === 'export' ? { 
              backgroundColor: '#3367d6', 
              borderColor: '#2a56c6' 
            } : {})
          }}
        >
          Save Phase 1 changes
        </button>

        <button 
          onClick={handleEmptyLoadImage}
          onMouseEnter={() => setHoveredButton('export')}
          onMouseLeave={() => setHoveredButton(null)}
          style={{
            ...primaryButtonStyle,
            marginLeft: 'auto', // Push to right side
            ...(hoveredButton === 'export' ? { 
              backgroundColor: '#3367d6', 
              borderColor: '#2a56c6' 
            } : {})
          }}
        >
          Load empty image
        </button>

        <button 
          onClick={handleLoadImage}
          onMouseEnter={() => setHoveredButton('export')}
          onMouseLeave={() => setHoveredButton(null)}
          style={{
            ...primaryButtonStyle,
            marginLeft: 'auto', // Push to right side
            ...(hoveredButton === 'export' ? { 
              backgroundColor: '#3367d6', 
              borderColor: '#2a56c6' 
            } : {})
          }}
        >
          Load image
        </button>

      </div>

      <div
        ref={containerRef}
        style={{
          flex: '1',
          border: '2px solid green',
          position: 'relative',
          overflow: 'auto', 
          backgroundColor: "black",
          width: '100%', 
          height: '100%', 
          WebkitOverflowScrolling: 'touch', 
          display: 'block'
        }}
        onWheel={(e) => {
          e.stopPropagation();
        }}
        onContextMenu={(e) => {
          e.preventDefault();
        }}
      >
        {!canvasLoaded && backgroundImageUrl && (
          <div style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255,255,255,0.7)',
            zIndex: 100
          }}>
            <p>Loading canvas...</p>
          </div>
        )}
        <div 
          ref={canvasContainerRef}
          style={{
            position: 'relative',
            minWidth: 'max-content', 
            display: 'inline-block',
            margin: '0 auto' 
          }}
        >
          <FabricJSCanvas 
            className="sample-canvas" 
            onReady={(canvas) => {
              onReady(canvas);
              canvas.enablePointerEvents = false;
            }}
          />
        </div>
      </div>
    </div>
    </>
  );
}

export default FabricCanvas;
